'use client';

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useUser } from '@clerk/nextjs';
import { getApiBase } from '@/lib/apiBase';

export type Theme = 'classic' | 'modern' | 'light';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useUser();
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      // Read from the data-theme attribute that the inline script already set
      const attr = document.documentElement.getAttribute('data-theme');
      if (attr === 'classic' || attr === 'modern' || attr === 'light') return attr;
      const stored = localStorage.getItem('theme');
      if (stored === 'classic' || stored === 'modern' || stored === 'light') return stored;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    return 'classic';
  });
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apply data-theme attribute to documentElement
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Load preference from API when user signs in
  useEffect(() => {
    if (!isSignedIn) {
      setHasLoadedPreference(false);
      // When not signed in, use localStorage value
      const stored = localStorage.getItem('theme') as Theme | null;
      if (stored === 'classic' || stored === 'modern' || stored === 'light') {
        setThemeState(stored);
      }
      return;
    }

    const loadPreference = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/preferences`);
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.theme) {
            setThemeState(data.preferences.theme);
          }
        }
      } catch (error) {
        console.error('Failed to load theme preference:', error);
      } finally {
        setHasLoadedPreference(true);
      }
    };

    loadPreference();
  }, [isSignedIn]);

  // Save preference to API (debounced)
  const savePreference = useCallback(async (newTheme: Theme) => {
    if (!isSignedIn) return;

    try {
      await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme: newTheme }),
      });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, [isSignedIn]);

  // Wrapper that updates state and persists to API
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);

    // Only save to API if signed in and initial load is complete
    if (isSignedIn && hasLoadedPreference) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        savePreference(newTheme);
      }, 300);
    }
  }, [isSignedIn, hasLoadedPreference, savePreference]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
